import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const UNASSIGNED = "__unassigned__";

export default function TransferLeadDialog({ lead, open, onOpenChange, onTransferred }) {
  const [targets, setTargets] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [pipeline, setPipeline] = useState("");
  const [assignee, setAssignee] = useState(UNASSIGNED);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPipeline("");
    setAssignee(lead?.assigned_to || UNASSIGNED);
    Promise.all([
      api.get("/admin/pipelines/targets").catch(() => ({ data: { targets: [] } })),
      api.get("/admin/counsellors").catch(() => ({ data: { counsellors: [] } })),
    ]).then(([t, c]) => {
      setTargets(t.data?.targets || []);
      setCounsellors(c.data?.counsellors || []);
    });
  }, [open, lead]);

  const submit = async () => {
    if (!pipeline) {
      toast.error("Pick a destination section");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/leads/${lead.id}/transfer`, {
        pipeline,
        assigned_to: assignee === UNASSIGNED ? null : assignee,
      });
      const t = targets.find((x) => x.id === pipeline);
      toast.success(`Moved to ${t?.name || "section"}`);
      onOpenChange(false);
      onTransferred?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transfer-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-[#1877F2]" /> Transfer lead
          </DialogTitle>
          <DialogDescription>
            Move <strong>{lead?.name || "this lead"}</strong> to another section and
            assign it to a team member.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Destination section</Label>
            <Select value={pipeline} onValueChange={setPipeline}>
              <SelectTrigger className="mt-1.5" data-testid="transfer-pipeline-select">
                <SelectValue placeholder="Choose a pipeline" />
              </SelectTrigger>
              <SelectContent>
                {targets.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`transfer-target-${t.id}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Assign to</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger className="mt-1.5" data-testid="transfer-assignee-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {counsellors.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`transfer-assignee-${c.id}`}>
                    {c.name || c.email} · {c.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="transfer-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !pipeline}
            className="bg-[#0A2540] hover:bg-[#1E3A5F]"
            data-testid="transfer-confirm"
          >
            {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
